import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiFolder, FiFolderPlus, FiCheck } = FiIcons;

const FolderAssignmentModal = ({ isOpen, search, onClose, onAssigned, user }) => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [error, setError] = useState('');

  // Load folders and check which ones already contain the search
  useEffect(() => {
    if (isOpen && search) {
      loadFoldersAndAssignments();
    }
  }, [isOpen, search]);

  const loadFoldersAndAssignments = async () => {
    if (!search) return;
    
    try {
      setLoading(true);
      
      // Get all folders
      const { data: foldersData, error: foldersError } = await supabase
        .from('search_folders')
        .select('*')
        .eq('user_id', user.id)
        .order('name');
        
      if (foldersError) throw foldersError;
      
      // Get folder assignments for this search
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('folder_searches')
        .select('folder_id')
        .eq('search_id', search.id);
        
      if (assignmentsError) throw assignmentsError;
      
      // Extract folder IDs that contain this search
      const assignedFolderIds = assignmentsData.map(assignment => assignment.folder_id);
      
      setFolders(foldersData || []);
      setSelectedFolders(assignedFolderIds);
    } catch (error) {
      console.error('Error loading folders and assignments:', error);
      setError('Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFolder = (folderId) => {
    setSelectedFolders(prev => 
      prev.includes(folderId)
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Please enter a folder name');
      return;
    }

    try {
      // Create new folder
      const { data, error } = await supabase
        .from('search_folders')
        .insert([
          { user_id: user.id, name: newFolderName.trim() }
        ])
        .select();
        
      if (error) throw error;
      
      // Add to folders list and select it
      setFolders([...folders, data[0]]);
      setSelectedFolders([...selectedFolders, data[0].id]);
      
      // Reset form
      setNewFolderName('');
      setIsCreatingFolder(false);
      setError('');
    } catch (error) {
      console.error('Error creating folder:', error);
      setError('Failed to create folder');
    }
  };

  const handleSave = async () => {
    try {
      // Get current assignments
      const { data: currentAssignments, error: fetchError } = await supabase
        .from('folder_searches')
        .select('folder_id')
        .eq('search_id', search.id);
        
      if (fetchError) throw fetchError;
      
      const currentFolderIds = currentAssignments.map(a => a.folder_id);
      
      // Determine folders to add and remove
      const foldersToAdd = selectedFolders.filter(id => !currentFolderIds.includes(id));
      const foldersToRemove = currentFolderIds.filter(id => !selectedFolders.includes(id));
      
      // Add new assignments
      if (foldersToAdd.length > 0) {
        const newAssignments = foldersToAdd.map(folderId => ({
          folder_id: folderId,
          search_id: search.id
        }));
        
        const { error: insertError } = await supabase
          .from('folder_searches')
          .insert(newAssignments);
          
        if (insertError) throw insertError;
      }
      
      // Remove old assignments
      if (foldersToRemove.length > 0) {
        for (const folderId of foldersToRemove) {
          const { error: deleteError } = await supabase
            .from('folder_searches')
            .delete()
            .eq('folder_id', folderId)
            .eq('search_id', search.id);
            
          if (deleteError) throw deleteError;
        }
      }
      
      if (onAssigned) {
        onAssigned();
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving folder assignments:', error);
      setError('Failed to save folder assignments');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-2xl shadow-xl max-w-md w-full"
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="bg-[#D59A4C20] p-2 rounded-full mr-3">
                  <SafeIcon icon={FiFolder} className="w-5 h-5 text-[#D59A4C]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Assign to Folders</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Search info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">{search?.keyword}</p>
              <p className="text-sm text-gray-500">
                {search?.results?.length || 0} opportunities
              </p>
            </div>
            
            {error && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            )}
            
            {/* Folder list */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-700">Select folders</h4>
                <button
                  onClick={() => setIsCreatingFolder(true)}
                  className="text-sm text-[#D59A4C] hover:text-[#B58C54] flex items-center"
                  disabled={isCreatingFolder}
                >
                  <SafeIcon icon={FiFolderPlus} className="w-3 h-3 mr-1" />
                  New Folder
                </button>
              </div>
              
              {/* Create folder form */}
              <AnimatePresence>
                {isCreatingFolder && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-3"
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <input
                        type="text"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder="Folder name"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D59A4C] focus:border-transparent"
                        autoFocus
                      />
                      <button
                        onClick={handleCreateFolder}
                        className="p-2 text-green-600 hover:text-green-800"
                      >
                        <SafeIcon icon={FiCheck} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setIsCreatingFolder(false);
                          setNewFolderName('');
                        }}
                        className="p-2 text-red-600 hover:text-red-800"
                      >
                        <SafeIcon icon={FiX} className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D59A4C]"></div>
                </div>
              ) : folders.length === 0 ? (
                <p className="text-gray-500 text-sm italic p-2">
                  No folders created yet. Create a new folder to organize your searches.
                </p>
              ) : (
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                  {folders.map((folder) => (
                    <div
                      key={folder.id}
                      className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => handleToggleFolder(folder.id)}
                    >
                      <div className="flex items-center">
                        <SafeIcon icon={FiFolder} className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="text-gray-800">{folder.name}</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedFolders.includes(folder.id)}
                        onChange={() => {}}
                        className="w-4 h-4 text-[#D59A4C] border-gray-300 rounded focus:ring-[#D59A4C]"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default FolderAssignmentModal;